import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Formik, Field, Form } from 'formik';
import get from 'lodash-es/get';

import { Box, Button, Text } from '@components/common';
import { InputField, CheckboxField, SelectField } from '@components/form';
import GooglePropertyField from '@containers/properties/GooglePropertyField';
import API from '@utils/api';
import validationSchema from './schema';
import AuthActions, { AuthSelectors } from '@redux/AuthRedux';
import AppActions from '@redux/AppRedux';
import { arry2KeyValue } from '@utils/object';
import { getErrors } from '@utils/error';
import { DEFAULT_BEDROOM_COUNTS } from '@services/minte';

class PropertyForm extends React.Component {
  static propTypes = {
    onSubmit: PropTypes.func,
    onNext: PropTypes.func,
    user: PropTypes.object
  };

  handleSubmit = async (values, actions) => {
    const { onSubmit, user } = this.props;
    const { setSubmitting, setErrors } = actions;
    setSubmitting(true);

    try {
      // eslint-disable-next-line no-unused-vars
      const { property, buildingName, bedroomsCount, ...restValues } = values;
      const buildingNameToSend = get(buildingName, 'value', undefined);

      const propertyReturned = await API.assignProperty({
        buildingName: buildingNameToSend,
        bedroomsCount: bedroomsCount.value,
        ...restValues
      });

      setSubmitting(false);
      onSubmit({
        ...user,
        propertyID: propertyReturned.propertyID,
        buildingName: get(buildingName, 'value', ''),
        bedroomsCount: bedroomsCount.value,
        ...restValues
      });
    } catch (err) {
      const error = getErrors(err);
      if (error.message) {
        setErrors({
          googlePlaceID: error.message
        });
      } else {
        setErrors({
          googlePlaceID: 'Would you please try again'
        });
      }
    }
    setSubmitting(false);
  };

  render() {
    return (
      <Formik
        initialValues={{
          googlePlaceID: '',
          property: null,
          bedroomsCount: null,
          buildingName: '',
          unitNumber: ''
        }}
        onSubmit={this.handleSubmit}
        validationSchema={validationSchema}
      >
        {({ isSubmitting, values }) => {
          const { property, googlePlaceID } = values;
          const hasMultipleBuildings =
            property &&
            property.buildingsNames &&
            property.buildingsNames.length > 0;

          const buildingNames =
            property && property.buildingsNames
              ? arry2KeyValue(property.buildingsNames)
              : [];
          const bedRooms = arry2KeyValue(
            property && property.bedroomsCounts
              ? property.bedroomsCounts
              : DEFAULT_BEDROOM_COUNTS,
            'Bedroom',
            {
              0: {
                value: 0,
                label: 'Studio'
              }
            }
          );

          return (
            <Form>
              <Box display="flex" flexDirection="column">
                <Text mb={4}>Tell us about your place</Text>

                <Field
                  mb={4}
                  component={GooglePropertyField}
                  name="googlePlaceID"
                  label="Enter your address"
                />

                {hasMultipleBuildings && (
                  <Field
                    component={SelectField}
                    mb={4}
                    name="buildingName"
                    label="Building"
                    options={buildingNames}
                  />
                )}
                <Field
                  component={InputField}
                  mb={4}
                  disabled={!googlePlaceID}
                  name="unitNumber"
                  type="text"
                  label="Unit Number"
                />
                <Field
                  component={SelectField}
                  disabled={!googlePlaceID}
                  isSearchable={false}
                  mb={4}
                  name="bedroomsCount"
                  label="Number of Bedrooms"
                  options={bedRooms}
                />
                <Field
                  component={CheckboxField}
                  type="checkbox"
                  mb={4}
                  name="hasPets"
                  label="Pets"
                />
                <Field
                  component={CheckboxField}
                  type="checkbox"
                  mb={4}
                  fontSize={0}
                  name="optInNewsLetter"
                  label="I want to receive service updates and promotional discounts via text"
                />
                <Field
                  component={CheckboxField}
                  type="checkbox"
                  mb={4}
                  fontSize={0}
                  name="optInText"
                  label="I want to receive promotions and offers via email"
                />
                <Text fontSize={0} mb={4} px={4}>
                  By selecting “Continue”, I state that I have read, understand,
                  and agree to The Minte’s Terms of Service, Permission to
                  Enter, and Privacy Policy.
                </Text>

                <Button flex={1} mb={4} type="submit" disabled={isSubmitting}>
                  Continue
                </Button>
              </Box>
            </Form>
          );
        }}
      </Formik>
    );
  }
}

const mapStatesToProps = state => ({
  user: AuthSelectors.selectCurrentUser(state)
});

const mapDispatchToProps = dispatch => ({
  onSubmit: newUserInfo => {
    dispatch(AuthActions.setUser(newUserInfo));
    dispatch(AppActions.setAppDataLoadRequest());
  }
});

export default connect(
  mapStatesToProps,
  mapDispatchToProps
)(PropertyForm);
